import { getDateWithoutTimezoneShift } from '@lender/utils';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { isNil, isString } from 'lodash';

import {
  COMMON_REQUEST_DATE_FORMAT,
  COMMON_REQUEST_DATETIME_FORMAT,
  EASTERN_TIME_ZONE,
} from 'constants/date';

import { getConditionalValue } from './data';

const NOON_HOUR = 12;
const SAME_DAY_ESIGNED_NUDGE_SECONDS = 2;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export const getCorrectedDate = (dateValue, { minDate, maxDate }) => {
  const newValue = dayjs(dateValue).isValid() ? dateValue : dayjs();

  const isMaxDateBeforeNewDate = !!maxDate && !!dayjs(maxDate).isBefore(newValue);
  const isMinDateAfterNewDate = !!minDate && !!dayjs(minDate).isAfter(newValue);

  if (isMaxDateBeforeNewDate) {
    return dayjs(maxDate);
  }

  if (isMinDateAfterNewDate) {
    return dayjs(minDate);
  }

  return newValue;
};

export const formatDate = (value, format, options) => {
  const { timeZone, includeLocalOffset } = options || {};

  const fixedDateWithTimeZone = timeZone
    ? dayjs(value).tz(timeZone)
    : dayjs(includeLocalOffset ? value : getDateWithoutTimezoneShift(value));

  return dayjs(value).isValid() ? fixedDateWithTimeZone.format(format) : value;
};

export const formatCalendarDate = (value, format) =>
  dayjs(value).isValid() ? dayjs.utc(value).format(format) : value;

// The API serializes date-only values as midnight UTC ("2026-10-08T00:00:00+00:00"); parsed as-is, a US
// browser lands on the previous evening and shows the day before. Read the calendar day as sent instead.
export const parseCalendarDate = value => {
  if (!value) {
    return null;
  }

  return isString(value)
    ? dayjs(dayjs.utc(value).format(COMMON_REQUEST_DATE_FORMAT))
    : dayjs(value);
};

// Today's US-Eastern calendar day as a local-midnight date (how a date field holds a day), whatever the browser's
// zone — the day the backends that run on Eastern time (MERS) call "today".
export const getEasternToday = () =>
  dayjs(dayjs().tz(EASTERN_TIME_ZONE).format(COMMON_REQUEST_DATE_FORMAT));

export const getCommonDateBody = date =>
  getConditionalValue(date, dayjs(date).format(COMMON_REQUEST_DATE_FORMAT));

export const getCommonDateTimeBody = date =>
  getConditionalValue(date, dayjs(date).utc(false).format(COMMON_REQUEST_DATETIME_FORMAT));

// FH-1435: anchor a date-only value at local noon so it keeps its calendar day when it round-trips through
// UTC and is redisplayed in the browser's time zone. A start/end-of-day anchor (00:00 / 23:59) crosses
// midnight at a real UTC offset and shifts the shown day by ±1; noon survives any real offset.
export const anchorDateAtNoon = date =>
  dayjs(date).hour(NOON_HOUR).minute(0).second(0).millisecond(0);

// The sent / e-signed date pair a form delivery records, both noon-anchored and formatted for the API. When
// e-signed lands on the same day as sent it is nudged forward a hair so the backend can still synthesize a
// read date strictly between them (SentDate < ReadDate < SignedDate). A nil e-signed stays null.
export const buildFormDeliveryDateWindow = (sentDate, eSignedDate) => {
  const sentAtNoon = anchorDateAtNoon(sentDate);
  const eSignedAtNoon = isNil(eSignedDate) ? null : anchorDateAtNoon(eSignedDate);
  const eSignedResolved =
    eSignedAtNoon && eSignedAtNoon.isSame(sentAtNoon)
      ? eSignedAtNoon.add(SAME_DAY_ESIGNED_NUDGE_SECONDS, 'second')
      : eSignedAtNoon;

  return {
    sentDate: sentAtNoon.format(COMMON_REQUEST_DATETIME_FORMAT),
    eSignedDate: isNil(eSignedResolved)
      ? null
      : eSignedResolved.format(COMMON_REQUEST_DATETIME_FORMAT),
  };
};
